import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Input,
  Paper,
  Flex,
  Text,
  ActionIcon,
  Title,
  Stack,
  Textarea,
  Center,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { IconPlus } from "../../shared/svg/AddIcon";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import {
  addHobbie,
  deleteHobbie,
  editHobbie,
  fetchHobbie,
} from "../../api/profileEditingApi";
import { HobbiesEdit } from "../../types/profile-editing";
import { TrashCanSmall } from "../../shared/svg/TrashCanSmall";
import { notifications } from "@mantine/notifications";
import { CheckIcon } from "../../shared/svg/CheckIcon";
import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { useMediaQuery } from "@mantine/hooks";
import ConfirmDelete from "../../utils/components/ConfirmDelete";

interface Props {
  setChanges: (t: boolean) => void;
}

const HobbiesEditingWidget: React.FC<Props> = ({ setChanges }) => {
  const { t } = useTranslation();
  const [hobbies, setHobbies] = useState<HobbiesEdit[]>([]);
  const [newHobbyName, setNewHobbyName] = useState("");
  const [newHobbyDescription, setNewHobbyDescription] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const matches = useMediaQuery("(max-width: 650px)");

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    const foo = async () => {
      try {
        const res = await fetchHobbie();
        setHobbies(res);
      } catch (err) {
        console.log(err);
      }
    };

    foo();
  }, []);

  const handleAddHobby = async () => {
    const name = newHobbyName.trim();
    const description = newHobbyDescription.trim();
    if (!name) {
      setErrorMessage(t("hobby_required"));
      return;
    }
    if (name) {
      try {
        const res = await addHobbie({ name, description });
        setHobbies([...hobbies, res]);
        setNewHobbyName("");
        setNewHobbyDescription("");
        setShowInput(false);
        setChanges(true);
        notifications.show({
          title: t("success"),
          message: t("add_successfully"),
          icon: <CheckIcon />,
          color: "green",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
      } catch (error: any) {
        if (error.errors.non_field_errors) {
          setErrorMessage(error.errors.non_field_errors[0]);
        } else if (error.errors.name) {
          setErrorMessage(error.errors.name[0]);
        } else {
          setErrorMessage(t("somethingWentWrong"));
        }
      }
    }
  };
  const handleEditHobby = (hobby: HobbiesEdit) => {
    setEditingId(hobby.id!);
    setNewHobbyName(hobby.name);
    setNewHobbyDescription(hobby.description || "");
    setShowInput(true);
  };

  const handleUpdateHobby = async () => {
    const name = newHobbyName.trim();
    const description = newHobbyDescription.trim();

    if (!name) {
      setErrorMessage(t("hobby_required"));
      return;
    }
    if (!name || editingId === null) return;

    try {
      const res = await editHobbie({ id: editingId, name, description });

      setHobbies((prev) =>
        prev.map((hobby) => (hobby.id === editingId ? res : hobby))
      );

      setNewHobbyName("");
      setNewHobbyDescription("");
      setEditingId(null);
      setShowInput(false);
      setChanges(true);

      notifications.show({
        title: t("success"),
        message: t("changed_successfully"),
        icon: <EditIconSmall />,
        color: "#CEB7FF",
      });
    } catch (error: any) {
      if (error.errors?.non_field_errors) {
        setErrorMessage(error.errors.non_field_errors[0]);
      } else if (error.errors.name) {
        setErrorMessage(error.errors.name[0]);
      } else {
        setErrorMessage(t("somethingWentWrong"));
      }
    }
  };

  const handleRemoveHobby = async (id: number) => {
    setChanges(true);
    notifications.show({
      title: t("success"),
      message: t("delete_successfully"),
      icon: <TrashCanSmall />,
      color: "#d3d3d3",
      style: {
        border: "1px solid #d3d3d3",
        borderRadius: "8px",
      },
    });
    setHobbies((prev) => prev.filter((hobby) => hobby.id !== id));
    if (id) {
      try {
        await deleteHobbie(id);
      } catch (error: any) {
        if (error.errors.non_field_errors) {
          setErrorMessage(error.errors.non_field_errors[0]);
        } else {
          setErrorMessage(t("somethingWentWrong"));
        }
      }
    }
  };
  const renderInputForm = () => (
    <Paper withBorder radius="md" p="md" mb="md">
      <Stack gap="xs">
        <Input.Wrapper
          label={t("enter_hobby_name") || "Hobby name"}
          withAsterisk
        >
          <Input
            value={newHobbyName}
            onChange={(e) => {
              setNewHobbyName(e.currentTarget.value);
              setErrorMessage(null);
            }}
            placeholder={t("enter_hobby_name") || "Hobby name"}
            autoFocus
            required
          />
        </Input.Wrapper>

        <Input.Wrapper label={t("enter_hobby_description") || "Description"}>
          <Textarea
            value={newHobbyDescription}
            placeholder={t("enter_hobby_description") || "Description"}
            onChange={(e) => {
              setNewHobbyDescription(e.currentTarget.value);
              setErrorMessage(null);
            }}
            autosize
            minRows={1}
            className="textarea-font"
          />
        </Input.Wrapper>

        <Flex justify="flex-end" gap="sm" align="center">
          {errorMessage && (
            <Text color="#B00020" size="xs" mt={4}>
              {errorMessage}
            </Text>
          )}

          <Button
            variant="light"
            style={{ flexShrink: 0 }}
            color="gray"
            onClick={() => {
              setShowInput(false);
              setNewHobbyName("");
              setNewHobbyDescription("");
              setEditingId(null);
              setErrorMessage(null);
            }}
          >
            {t("cancel") || "Cancel"}
          </Button>

          <Button
            onClick={editingId ? handleUpdateHobby : handleAddHobby}
            style={{ flexShrink: 0 }}
            disabled={!newHobbyName.trim()}
          >
            {editingId ? t("save") : t("save_hobby")}
          </Button>
        </Flex>
      </Stack>
    </Paper>
  );

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mb={matches ? undefined : "lg"}
        direction={matches ? "column" : "row"}
        gap={20}
      >
        <Title size={28}>{t("your_hobbies")}</Title>
        <Button
          mb="md"
          leftSection={<IconPlus />}
          variant="light"
          onClick={() => {
            setShowInput(true);
            setErrorMessage(null);
          }}
          disabled={showInput}
        >
          {t("add_hobby")}
        </Button>
      </Flex>

      {showInput && !editingId && renderInputForm()}

      {hobbies.length > 0 ? (
        <Flex direction="column" gap="sm">
          {hobbies.map((hobby) =>
            editingId === hobby.id ? (
              renderInputForm()
            ) : (
              <Paper
                key={hobby.id}
                withBorder
                px={16}
                py={12}
                radius="md"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "rgb(245, 243, 250)",
                }}
              >
                <Box
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    maxWidth: "100%",
                  }}
                >
                  <Text fw={600}>{hobby.name}</Text>
                  <Text
                    size="sm"
                    c="dimmed"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                      maxWidth: "100%",
                    }}
                  >
                    {hobby.description}
                  </Text>
                </Box>
                <Flex gap="xs">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => handleEditHobby(hobby)}
                  >
                    <EditIconSmall />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => {
                      if (hobby.id) {
                        setIsDeleteOpen(true);
                        setDeleteId(hobby.id);
                      }
                      setErrorMessage(null);
                    }}
                  >
                    <ClearIcon />
                  </ActionIcon>
                </Flex>
              </Paper>
            )
          )}
        </Flex>
      ) : (
        !showInput && (
          <Center py="xl">
            <Paper
              radius="md"
              p="xl"
              withBorder
              shadow="sm"
              style={{ maxWidth: 1000, width: "100%" }}
            >
              <Flex
                align="center"
                w="100%"
                px={20}
                gap={matches ? 20 : 40}
                direction={matches ? "column" : "row"}
              >
                <Title
                  order={2}
                  fw={700}
                  w={matches ? "100%" : "33%"}
                  ta="left"
                  style={{ fontSize: "1.5rem" }}
                >
                  {t("nothing_here")}
                </Title>
                <Text
                  size="xl"
                  c="dimmed"
                  w={matches ? "100%" : "66%"}
                  style={{ fontSize: "1.125rem" }}
                >
                  {t("no_hobbies")}
                </Text>
              </Flex>
            </Paper>
          </Center>
        )
      )}
      <ConfirmDelete
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        onDelete={() => {
          if (deleteId !== null) handleRemoveHobby(deleteId);
        }}
        title={t("delete_hobby_title")}
      />
    </Box>
  );
};

export default HobbiesEditingWidget;
